import _ from 'lodash';
import { DateTime } from 'luxon';
import {
  createOrRequestOrder,
  updateOrder,
  cancelOrder,
  approveOrder,
  hasAllOrderSubmissionBodyFieldValues,
  hasAnyOrderSubmissionBodyFieldValues, updateAndConfirmOrder,
} from './orders-dialog-context-update-order-actions';
import { initializeDate } from './orders-dialog-context-initialize';
import { getOrdersAllocations, getSelectNewOrderEntityState } from './orders-dialog-context-utilities';
import { wrapAndValidateAsyncAction } from '~/common/bn-context/bn-context-helpers';

const clearableState = {
  initializationState: 'pre-initialization',

  // Specify an orderId, this will let you edit the order
  // and view all other orders on the site
  selectedOrderId: undefined,
  newDataEntered: false,
  canCreateOrRequestOrder: false,
  createOrEditSirenAction: undefined,
  approveSirenAction: undefined,
  errorMessagesByField: {},
  isEditingNote: false,

  // Initially required data
  // Specify a date & a barn, this will allow you to create a new order
  // and view all other orders on the site
  specifyingNewOrder: false,
  isoDate: undefined,
  isoDateNoon: undefined,
  isoDateNoonUTC: undefined,
  usersCurrentLocalIsoDate: undefined,
  selectedBarnDaysOrders: [],
  forecastsByBinId: {},

  // While editing / creating an order, all changes on-top of the potentially existing order live here
  unsavedChanges: {},
  hasUnsavedChanges: false,

  currentTab: 'orders',
};

const persistentState = {
  // optional callback whenever the orders endpoint is updated
  onOrdersUpdate: undefined,
  // optional callback whenever an order is closed
  onOrderClose: undefined,
  // callback when dialog closes, called when order is saved or canceled
  onDialogClose: undefined,
  enqueueSnackbar: undefined,

  // How many submits are in progress
  submitting: 0,

  selectedBarnEntity: undefined,
  selectedBarnOrdersEntity: undefined,
  selectedBarnOrganizationBinEntity: undefined,
  selectedBarnOrganizationBinLevelsEntity: undefined,
  selectedOrderEntity: undefined,
  selectedAllocations: [],
  selectedBarnDaysAllocations: [],
  selectedBarnsBins: [],

  binLevelsByBinId: {},
  barnBinLevelEntities: [],

  feeds: [],

  selectedBarnId: undefined,
  barns: { // Map of data loaded for barns
    // [barnId]: {
    // barnEntity: ...
    // binEntities: ...
    // organizationBinEntity: ...
    // ordersEntity: ...
  },

  daysOrders: [],
  daysAllocations: [],

  feedOrdersEntity: undefined,

  // This data is filled in during initialization & navigation
  // barnsOrdersEntity: undefined,
  siteEntity: undefined,
  siteDayOrderEntities: [],

  developerErrorMessage: undefined,
  initializationErrorMessage: undefined,
  hasSuggestions: false,
};

export const initialState = {
  ...clearableState,
  ...persistentState,
};

const validStateKeys = Object.keys(initialState);
const validateState = (state) => {
  const stateKeys = Object.keys(state);
  const missingOrIncorrectlyDefinedKeys = _.xor(stateKeys, validStateKeys);
  if (missingOrIncorrectlyDefinedKeys.length > 0) {
    console.error(`Invalid or missing state keys found: ${missingOrIncorrectlyDefinedKeys}`);
    state.developerErrorMessage = `Invalid or missing state keys found: ${missingOrIncorrectlyDefinedKeys}`;
  }

  return state;
};

const getErrorMessages = ({ state, changes }) => {
  const { usersCurrentLocalIsoDate } = state;
  const changedFields = Object.keys(changes);
  const errorMessagesByField = _.omit(state.errorMessagesByField, changedFields);
  if (changedFields.find(field => field === 'deliveryDate')) {
    const isInPast = changes.deliveryDate < DateTime.fromISO(usersCurrentLocalIsoDate).toFormat('yyyy-MM-dd');
    if (isInPast) {
      errorMessagesByField.deliveryDate = 'Delivery date cannot be in the past';
    }
    if (!changes.deliveryDate) {
      errorMessagesByField.deliveryDate = 'Delivery date is required';
    }
  }
  return errorMessagesByField;
};

export const ordersDialogContextReducer = {
  'clear-state': (state) => {
    return validateState({
      ...state,
      ...clearableState,
    });
  },
  'change-tab': (state, action) => {
    return { ...state, currentTab: action.newTab };
  },
  'set-initialization-error-message': (state, action) => {
    return { ...state, initializationErrorMessage: action.initializationErrorMessage };
  },
  'developer-error': (state, action) => {
    console.error(action.developerErrorMessage);
    return { ...state, developerErrorMessage: action.developerErrorMessage };
  },
  'start-initialization': (state) => {
    return { ...state, initializationState: 'loading' };
  },
  'set-initialization-data': (state, action) => {
    const errorData = {};
    if (state.initializationState !== 'loading') {
      errorData.developerErrorMessage = `Attempting to set-initialization-data when initializationState is not loading, initializationState: ${state.initializationState}`;
      console.error(errorData.developerErrorMessage);
    }

    return { ...state, ...errorData, ...action.data };
  },
  'initialize-barn-data': (state, action) => {
    return {
      ...state,
      ...action.data,
    };
  },
  'complete-initialization': (state, action) => {
    const errorData = {};
    if (state.initializationState !== 'loading') {
      errorData.developerErrorMessage = `Attempting to complete-initialization when initializationState is not loading, initializationState: ${state.initializationState}`;
      console.error(errorData.developerErrorMessage);
    }

    const updatedState = {
      ...state,
      ...action.data,
      ...errorData,
      initializationState: 'complete',
    };

    return {
      ...updatedState,
      ...getSelectNewOrderEntityState({ state: updatedState, newOrderEntity: updatedState.selectedOrderEntity }),
    };
  },
  'set-days-orders': (state, action) => {
    return {
      ...state,
      daysOrders: action.daysOrders,
    };
  },
  'enable-editing-note': (state) => {
    return {
      ...state,
      isEditingNote: true,
    };
  },
  'add-new-order': (state, action) => {
    const newOrderEntity = action.order;
    const newOrderId = newOrderEntity.getLinkByRel('self').href;
    const newDaysAllocations = state.daysAllocations.filter(allocation => allocation.orderId.href !== newOrderId);
    newDaysAllocations.push(...getOrdersAllocations(newOrderEntity));

    const updatedState = {
      ...state,
      daysOrders: [action.order, ...state.daysOrders],
      daysAllocations: newDaysAllocations,
    };

    return {
      ...updatedState,
      ...getSelectNewOrderEntityState({ state: updatedState, newOrderEntity }),
    };
  },
  'remove-order': (state, action) => {
    const updatedState = {
      ...state,
      daysOrders: state.daysOrders.filter(item => item.getLinkByRel('self').href !== action.orderId),
      daysAllocations:  state.daysAllocations.filter(item => item.orderId !== action.orderId),
    };

    return {
      ...updatedState,
      ...getSelectNewOrderEntityState({ state: updatedState }),
    };
  },
  'select-order': (state, action) => {
    return {
      ...state,
      ...getSelectNewOrderEntityState({ state, newOrderEntity: action.selectedOrderEntity }),
    };
  },
  'specify-new-order': (state) => {
    return {
      ...state,
      ...getSelectNewOrderEntityState({ state }),
    };
  },
  'set-unsaved-change-data': (state, action) => {
    if (!state.createOrEditSirenAction) {
      return { ...state };
    }
    const preUpdatedState = {
      ...state,
      unsavedChanges: {
        ...state.unsavedChanges,
        ...action.data,
      },
      errorMessagesByField: getErrorMessages({ state, changes: action.data }),
    };

    const sirenAction = preUpdatedState.specifyingNewOrder
      ? preUpdatedState.selectedBarnOrdersEntity?.getAction('create-order') || preUpdatedState.selectedBarnOrdersEntity?.getAction('request-order')
      : preUpdatedState.selectedOrderEntity?.getAction('update-order');

    return {
      ...preUpdatedState,

      newDataEntered: hasAllOrderSubmissionBodyFieldValues({ state: preUpdatedState, action: sirenAction }),
      hasUnsavedChanges: hasAnyOrderSubmissionBodyFieldValues({ state: preUpdatedState, action: sirenAction }),
    };
  },
  'submit-start': (state) => {
    return { ...state, submitting: (state.submitting || 0) + 1 };
  },
  'submit-complete': (state) => {
    return { ...state, submitting: state.submitting - 1 };
  },
  'create-or-request-order': wrapAndValidateAsyncAction(createOrRequestOrder, initialState),
  'approve-order': wrapAndValidateAsyncAction(approveOrder, initialState),
  'update-order': wrapAndValidateAsyncAction(updateOrder, initialState),
  'update-and-confirm-order': wrapAndValidateAsyncAction(updateAndConfirmOrder, initialState),
  'cancel-order': wrapAndValidateAsyncAction(cancelOrder, initialState),
  'select-date': wrapAndValidateAsyncAction(initializeDate, initialState),
};

export const defaultHandler = (state, action) => {
  const developerErrorMessage = `An action was dispatched which matches no known action type: ${action.type}`;
  console.error(developerErrorMessage);
  return { ...state, developerErrorMessage };
};
